<template>
  <transition name="modal">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-md"
        @click="close"
      ></div>

      <!-- Modal Content -->
      <div
        class="relative w-full max-w-lg bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-6 md:p-8 animate-scale-up overflow-hidden"
      >
        <!-- Decoration bits -->
        <div class="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl"></div>
        <div class="absolute -bottom-10 -left-10 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl"></div>

        <!-- Header -->
        <div class="relative text-center mb-8">
          <div class="w-20 h-20 mx-auto mb-4 rounded-3xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-3xl font-bold shadow-lg ring-2 ring-white/20">
            {{ userInitials }}
          </div>
          <h2 class="text-2xl font-bold text-white mb-2 tracking-tight">Update Profile</h2>
          <p class="text-white/60 text-sm font-medium">Customize your presence on Wedding Ledger</p>
          
          <button
            @click="close"
            class="absolute -top-2 -right-2 text-white/40 hover:text-white transition-all bg-white/5 hover:bg-white/10 p-2 rounded-full border border-white/10"
          >
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Form -->
        <div class="relative space-y-5">
          <div class="space-y-2">
            <label class="block text-xs font-bold text-white/40 uppercase tracking-widest px-1">Display Name</label>
            <div class="relative group">
               <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-white/30 group-focus-within:text-purple-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input 
                v-model="displayName"
                type="text" 
                placeholder="Your name"
                class="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 pl-11 pr-4 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
              />
            </div>
          </div>

          <div class="space-y-2 opacity-60">
            <label class="block text-xs font-bold text-white/40 uppercase tracking-widest px-1">Email Address</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <input 
                :value="user?.email"
                type="email" 
                disabled
                class="w-full bg-white/5 border border-white/10 rounded-2xl py-3.5 pl-11 pr-4 text-white/50 cursor-not-allowed"
              />
            </div>
            <p class="text-[10px] text-white/30 italic px-1">Email cannot be changed for security</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-10 flex gap-3">
          <button
            @click="close"
            class="flex-1 py-4 px-6 rounded-2xl font-bold text-sm text-white/60 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 transition-all"
          >
            Cancel
          </button>
          <button
            @click="handleSave"
            :disabled="isSaving"
            class="flex-2 py-4 px-10 rounded-2xl font-bold text-sm text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 shadow-[0_8px_20px_rgba(168,85,247,0.4)] hover:shadow-[0_8px_25px_rgba(168,85,247,0.6)] disabled:opacity-50 disabled:cursor-not-allowed transition-all relative overflow-hidden group"
          >
            <span v-if="!isSaving" class="relative z-10 flex items-center justify-center gap-2">
              Save Changes
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span v-else class="flex items-center justify-center gap-2">
              <svg class="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          </button>
        </div>
      </div>
    </div>
  </transition>

  <ToastedVue 
      :showToast="showToast"
    :isFailed="isFailed" 
    :informMessage="toastMessage" 
    @onClose="showToast = false" 
  />
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { doc, updateDoc } from 'firebase/firestore'
import { projectFirestore } from '@/firebase/config'
import ToastedVue from '@/components/ToastedVue.vue'
import useCollection from '@/composables/useDocument'

const props = defineProps({
  isOpen: Boolean,
  user: Object,
  onClose: Function
})

const emit = defineEmits(['updated'])

const displayName = ref('')
const isSaving = ref(false)
const showToast = ref(false)
const isFailed = ref(false)
const toastMessage = ref('')

// Initialize form
watch(() => props.isOpen, (newVal) => {
  if (newVal && props.user) {
    displayName.value = props.user.displayName || ''
  }
})

const userInitials = computed(() => {
  if (!displayName.value) return '??'
  const names = displayName.value.split(' ')
  return names.length >= 2 
    ? (names[0][0] + names[names.length - 1][0]).toUpperCase()
    : displayName.value.substring(0, 2).toUpperCase()
})

const close = () => {
  props.onClose()
}

const {updateDocs}=useCollection('users');
const handleSave = async () => {
  if (!displayName.value.trim()) {
    isFailed.value = true
    toastMessage.value = 'Please enter a display name'
    showToast.value = true
    return
  }

  isSaving.value = true
  try {

    await updateDocs(props.user.id, {
      displayName: displayName.value
    })
    
    toastMessage.value = 'Profile updated successfully!'
    isFailed.value = false
    showToast.value = true
    
    emit('updated')
    setTimeout(() => {
      close()
    }, 1500)
  } catch (error) {
    console.error('Error updating profile:', error)
    isFailed.value = true
    toastMessage.value = 'Failed to update profile. Please try again.'
    showToast.value = true
  } finally {
    isSaving.value = false
  }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.animate-scale-up {
  animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>
